import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './CommonStyles.css';
import './AllSellStyles.css';
import './Products.css'; // Import Products CSS for filters
import './ProductsActions.css';
import './MobileResponsive.css';
import ViewSaleModal from './ViewSaleModal';
import EditShippingModal from './EditShippingModal';
import SellReturnModal from './SellReturnModal';
import SendNotificationModal from './SendNotificationModal';
import LedgerModal from './LedgerModal';
import DateFilter from '../DateFilter';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import dateUtils from '../../utils/dateUtils';

const AllSell = ({ searchTerm = '', initialTab = 'all_sales' }) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  // searchTerm is now a prop
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showInvoiceUrlModal, setShowInvoiceUrlModal] = useState(false);
  const [invoiceUrlToShow, setInvoiceUrlToShow] = useState('');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [selectedSaleForPayment, setSelectedSaleForPayment] = useState(null);
  const [paymentData, setPaymentData] = useState({
    method: 'Cash',
    paidOn: new Date().toISOString().slice(0, 16),
    amount: '',
    account: 'None',
    document: null,
    cardNumber: '',
    cardHolderName: '',
    cardExpiry: '',
    cardCvv: '',
    checkNo: '',
    checkExpiryDate: '',
    bankSubmissionDate: '',
    checkImage: null
  });
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [selectedSaleForViewPayment, setSelectedSaleForViewPayment] = useState(null);
  const [showEditShippingModal, setShowEditShippingModal] = useState(false);
  const [selectedSaleForShipping, setSelectedSaleForShipping] = useState(null);
  const [showSellReturnModal, setShowSellReturnModal] = useState(false);
  const [selectedSaleForReturn, setSelectedSaleForReturn] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedSaleForNotification, setSelectedSaleForNotification] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPackingSlipModal, setShowPackingSlipModal] = useState(false);
  const [packingSlipData, setPackingSlipData] = useState(null);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  const [salesError, setSalesError] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });
  const [sortConfig, setSortConfig] = useState({ key: 'saleDate', direction: 'desc' });
  const [editingPayment, setEditingPayment] = useState(null); // payment being edited
  const [editPaymentData, setEditPaymentData] = useState({});
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [selectedSaleForLedger, setSelectedSaleForLedger] = useState(null);
  const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
  const [deliveryNoteData, setDeliveryNoteData] = useState(null);

  // Discount modal state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [discountForm, setDiscountForm] = useState({
    name: '',
    products: [],
    brand: '',
    category: '',
    location: '',
    priority: '',
    discountType: '',
    discountAmount: '',
    startsAt: '',
    endsAt: '',
    sellingPriceGroup: 'All',
    applyInCustomerGroups: false,
    isActive: false,
  });

  // Payment accounts state
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [isLoadingPaymentAccounts, setIsLoadingPaymentAccounts] = useState(false);

  // Filter States
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState(() => {
    const today = dateUtils.getChicagoToday();
    return [today, today];
  });
  const [startDate, endDate] = dateRange;
  const [filterUser, setFilterUser] = useState('all');
  const [filterCommissionAgent, setFilterCommissionAgent] = useState('all');
  const [filterShippingStatus, setFilterShippingStatus] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState(false);
  
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync activeTab with initialTab prop when it changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  // State for dropdown options
  const [businessLocations, setBusinessLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Derived options for select dropdowns
  const uniqueAgents = [...new Set(sales.map(s => s.commissionAgent).filter(a => a !== 'N/A'))];

  // For display names in filters, we use the already fetched contacts, users, and locations.
  // The filter logic uses IDs, so we just need to ensure the select values match those IDs.

  // Fetch initial data on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch Business Locations
        const locationsPromise = apiService.getBusinessLocations()
          .catch(err => { console.error('Error fetching locations:', err); return []; });

        // Fetch Users
        const usersPromise = apiService.getUsers()
          .catch(err => { console.error('Error fetching users:', err); return []; });

        // Fetch Contacts
        const contactsPromise = apiService.getContacts()
          .catch(err => { console.error('Error fetching contacts:', err); return []; });

        const [locationsRaw, usersRaw, contactsRaw] = await Promise.all([locationsPromise, usersPromise, contactsPromise]);

        const extractArray = (res) => {
          if (!res) return [];
          if (Array.isArray(res)) return res;
          if (Array.isArray(res.data)) return res.data;
          if (res.data && Array.isArray(res.data.data)) return res.data.data;
          if (Array.isArray(res.users)) return res.users;
          if (Array.isArray(res.contacts)) return res.contacts;
          if (Array.isArray(res.locations)) return res.locations;
          return [];
        };

        const locations = extractArray(locationsRaw);
        console.log('[AllSell] Available Locations:', locations);
        setBusinessLocations(locations);
        setUsers(extractArray(usersRaw));
        setContacts(extractArray(contactsRaw));
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  // Load payment accounts
  const loadPaymentAccounts = async () => {
    try {
      setIsLoadingPaymentAccounts(true);
      const accountsData = await apiService.getPaymentAccounts();
      const accounts = accountsData.data || accountsData || [];
      console.log('Payment accounts from API in AllSell (already deduplicated):', accounts);
      setPaymentAccounts(accounts);
    } catch (error) {
      console.error('Error loading payment accounts:', error);
    } finally {
      setIsLoadingPaymentAccounts(false);
    }
  };

  // Load payment accounts on component mount
  useEffect(() => {
    if (apiService.isTokenValid()) {
      loadPaymentAccounts();
    }
  }, []);

  // Transform API data to match UI structure
  const transformSaleData = (apiSales) => {
    let rawSales = [];
    if (Array.isArray(apiSales)) {
      rawSales = apiSales;
    } else if (apiSales && Array.isArray(apiSales.data)) {
      rawSales = apiSales.data;
    } else if (apiSales && apiSales.data && Array.isArray(apiSales.data.data)) {
      rawSales = apiSales.data.data;
    }

    console.log(`[AllSell] Extracted ${rawSales.length} raw sales`);

    return rawSales.map(sale => {
      const totalPayable = parseFloat(sale.final_total || 0);
      const totalPaid = sale.payment_lines?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 
                       parseFloat(sale.total_paid || 0);
      return {
        id: sale.id,
        invoiceNo: sale.invoice_no,
        customer: sale.contact?.name || 'Walk-In Customer',
        contactNumber: sale.contact?.mobile || 'N/A',
        location: sale.location?.name || 'N/A',
        paymentStatus: sale.payment_status,
        totalPayable: totalPayable.toFixed(2),
        totalPaid: totalPaid.toFixed(2),
        sellDue: Math.max(0, totalPayable - totalPaid).toFixed(2),
        paymentMethod: sale.payment_lines?.[0]?.method || 'N/A',
        date: (sale.transaction_date || sale.sale_date || sale.created_at || '').substring(0, 10) || 'N/A',
        shippingStatus: sale.shipping_status || 'N/A',
        staff: sale.created_by_name || 'N/A',
        apiData: sale,
        saleDate: sale.transaction_date || sale.sale_date || sale.created_at
      };
    });
  };

  const loadSales = async () => {
    setIsLoadingSales(true);
    setSalesError('');
    try {
      // Match live website params exactly — this format is confirmed to work:
      // per_page=-1, full datetime string for dates, status=final for all_sales
      const params = {
        per_page: -1,
        start_date: startDate ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}` : '',
        end_date: endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}` : '',
        status: activeTab === 'list_quotation' ? 'quotation' : (activeTab === 'list_draft' ? 'draft' : (activeTab === 'all_sales' ? 'final' : '')),
        location_id: filterLocation !== 'all' ? filterLocation : '',
        contact_id: filterCustomer !== 'all' ? filterCustomer : '',
        payment_status: filterStatus !== 'all' ? filterStatus : '',
        order_by: 'transaction_date',
        direction: 'desc'
      };

      console.log('[AllSell] VERSION 2.0 - Fetching with params:', params);
      const apiSalesData = activeTab === 'sales_returns'
        ? await apiService.getSellReturns(params)
        : await apiService.getSales(params);
      console.log('[AllSell] Raw API Response:', apiSalesData);
      
      const salesArray = Array.isArray(apiSalesData) ? apiSalesData : (apiSalesData?.data || []);
      console.log('[AllSell] Sales count from API:', salesArray.length);
      
      // Log the IDs of the first few sales to see if the new one is there
      if (salesArray.length > 0) {
        console.log('[AllSell] First 3 sales IDs:', salesArray.slice(0, 3).map(s => s.id || s.transaction_id));
        console.log('[AllSell] First 3 sales Statuses:', salesArray.slice(0, 3).map(s => s.status));
        console.log('[AllSell] First 3 sales Location IDs:', salesArray.slice(0, 3).map(s => s.location_id));
        console.log('[AllSell] First sale date:', salesArray[0].transaction_date || salesArray[0].sale_date);
      }

      const transformedSales = transformSaleData(apiSalesData);
      setSales(transformedSales);
      setFilteredSales(transformedSales);
    } catch (error) {
      setSalesError('Failed to load sales');
      console.error('Error loading sales:', error);
    } finally {
      setIsLoadingSales(false);
    }
  };



  useEffect(() => {
    loadSales();
  }, [filterLocation, filterCustomer, filterStatus, dateRange, activeTab]);

  // Re-fetch sales whenever navigated back from AddSell with a reloadSales signal
  useEffect(() => {
    if (routerLocation?.state?.reloadSales) {
      loadSales();
    }
  }, [routerLocation?.state?.reloadSales]);

  // Local search filter only — all other filters are handled by the API
  useEffect(() => {
    if (!localSearchTerm) {
      setFilteredSales(sales);
      return;
    }
    const lower = localSearchTerm.toLowerCase();
    setFilteredSales(
      sales.filter(s =>
        (s.invoiceNo || '').toLowerCase().includes(lower) ||
        (s.customer || '').toLowerCase().includes(lower)
      )
    );
  }, [sales, localSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openDropdownId) return;

      const clickedActionButton = event.target.closest('.actions-btn');
      const clickedDropdownMenu = event.target.closest('.action-dropdown-menu');
      if (clickedActionButton || clickedDropdownMenu) return;

      setOpenDropdownId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedSales = (salesToSort) => {
    if (!sortConfig.key) return salesToSort;

    return [...salesToSort].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to string for comparison if needed
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle numeric values
      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;
    try {
      await apiService.deleteSale(saleId);
      setOpenDropdownId(null);
      toast.success('Sale deleted successfully.');
      loadSales();
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error(`Failed to delete sale: ${error.message}`);
    }
  };

  const handleConvertToProforma = async (sale) => {
    try {
      const saleId = sale.apiData?.id || sale.id;
      if (!window.confirm('Are you sure you want to convert this to a Proforma Invoice?')) return;
      
      await apiService.convertToProforma(saleId);
      toast.success('Converted to Proforma Invoice successfully.');
      loadSales();
    } catch (error) {
      console.error('Error converting to proforma:', error);
      toast.error(`Failed to convert: ${error.message}`);
    }
    setOpenDropdownId(null);
  };

  const handleCopyQuotation = async (sale) => {
    try {
      const saleId = sale.apiData?.id || sale.id;
      const response = await apiService.getSale(saleId);
      const saleData = response.data?.[0] || response.data || response || {};
      
      const payload = {
        contact_id: saleData.contact_id,
        location_id: saleData.location_id,
        status: 'draft',
        sub_status: 'quotation',
        is_quotation: 1,
        final_total: parseFloat(saleData.final_total || 0),
        sale_date: dateUtils.formatDateForApi(new Date()),
        transaction_date: dateUtils.formatDateForApi(new Date()),
        tax_rate_id: saleData.tax_id,
        tax_amount: parseFloat(saleData.tax_amount || 0),
        discount_type: saleData.discount_type || 'fixed',
        discount_amount: parseFloat(saleData.discount_amount || 0),
        products: (saleData.sell_lines || []).map(line => ({
          product_id: line.product_id,
          variation_id: line.variation_id,
          quantity: parseFloat(line.quantity),
          unit_price: parseFloat(line.unit_price)
        }))
      };
      
      await apiService.saveSell(payload);
      toast.success('Quotation copied successfully.');
      loadSales();
    } catch (error) {
      console.error('Error copying quotation:', error);
      toast.error(`Failed to copy: ${error.message}`);
    }
    setOpenDropdownId(null);
  };

  const toggleDropdown = (saleId, event) => {
    event.stopPropagation(); // Prevent event bubbling
    if (openDropdownId === saleId) {
      setOpenDropdownId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();

      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenDropdownId(saleId);
    }
  };

  const formatDate = (dateString) => {
    return dateUtils.formatDate(dateString);
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `$${num.toFixed(2)}`;
  };

  const handleExport = (format) => {
    const salesToExport = typeof getSortedSales === 'function' ? getSortedSales(filteredSales) : filteredSales;

    if (!salesToExport || salesToExport.length === 0) {
      toast.warning('No sales data to export');
      return;
    }

    const headers = ['Invoice No', 'Date', 'Customer', 'Contact Number', 'Location', 'Payment Status', 'Payment Method', 'Total Payable', 'Total Paid', 'Sell Due', 'Shipping Status', 'Created By'];
    const rows = salesToExport.map(s => [
      s.invoiceNo || '',
      s.saleDate || '',
      s.customer || '',
      s.contactNumber || '',
      s.location || '',
      s.paymentStatus || '',
      s.paymentMethod || '',
      (parseFloat(s.totalPayable || 0)).toFixed(2),
      (parseFloat(s.totalPaid || 0)).toFixed(2),
      (parseFloat(s.sellDue || 0)).toFixed(2),
      s.shippingStatus || '',
      s.createdBy || ''
    ]);

    const title = 'Sales List';
    const filename = 'sales';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();
  };

  const handlePrint = () => {
    printPage();
  };

  const handleColumnVisibility = () => {
    // Placeholder for column visibility
    toast.info('Column visibility - functionality to be implemented');
  };

  const handleViewSale = (sale) => {
    if (!sale) return;
    setSelectedSale(sale);
    setShowViewModal(true);
    setOpenDropdownId(null);
  };

  const handleEditSale = (sale) => {
    console.log('Edit sale:', sale);
    setOpenDropdownId(null);
    const apiData = sale.apiData || sale;
    if (apiData.sub_status === 'quotation') {
      navigate('/sell/add-quotation', { state: { editSale: sale } });
    } else if (apiData.status === 'draft' && apiData.sub_status !== 'proforma') {
      navigate('/sell/add-draft', { state: { editSale: sale } });
    } else {
      navigate('/sell/add', { state: { editSale: sale } });
    }
  };

  const handleEditShipping = (sale) => {
    console.log('Edit shipping:', sale);
    setSelectedSaleForShipping(sale);
    setShowEditShippingModal(true);
    setOpenDropdownId(null);
  };

  const handlePrintInvoice = (sale) => {
    console.log('Print invoice:', sale);
    const invoiceUrl = sale.apiData?.invoice_url || sale.invoiceUrl;
    if (invoiceUrl) {
      // Open invoice URL in new tab for printing
      window.open(invoiceUrl, '_blank');
    } else {
      toast.error('Invoice URL not available for this sale.');
    }
    setOpenDropdownId(null);
  };

  const handleDeliveryNote = (sale) => {
    const apiData = sale.apiData || sale;
    const items = apiData.sell_lines || sale.products || [];
    setDeliveryNoteData({
      invoiceNo: sale.invoiceNo || apiData.invoice_no || 'N/A',
      date: dateUtils.formatDate(new Date()),
      shippingStatus: sale.shippingStatus || apiData.shipping_status || 'Pending',
      deliveryPerson: apiData.delivery_person || 'N/A',
      customer: sale.customer || apiData.contact?.name || 'Walk-In Customer',
      contact: sale.contactNumber || apiData.contact?.mobile || 'N/A',
      address: apiData.shipping_address || apiData.contact?.address_line_1 || 'N/A',
      items: items.map((item, index) => ({
        index: index + 1,
        name: item.product?.name || item.name || 'N/A',
        sku: item.product?.sku || item.sku || 'N/A',
        qty: `${item.quantity || 0} ${item.unit?.short_name || 'pcs'}`,
        condition: 'Good ✓',
      })),
    });
    setShowDeliveryNoteModal(true);
    setOpenDropdownId(null);
  };

  const handleViewPayments = (sale) => {
    console.log('View payments:', sale);
    setSelectedSaleForViewPayment(sale);
    setShowViewPaymentModal(true);
    setOpenDropdownId(null);
  };

  const handleSellReturn = (sale) => {
    console.log('Sell return:', sale);
    setSelectedSaleForReturn(sale);
    setShowSellReturnModal(true);
    setOpenDropdownId(null);
  };

  const handleInvoiceUrl = (saleId) => {
    const sale = sales.find(s => s.id === saleId);
    if (sale) {
      const invoiceUrl = sale.apiData?.invoice_url || sale.invoiceUrl;
      if (invoiceUrl) {
        setInvoiceUrlToShow(invoiceUrl);
        setShowInvoiceUrlModal(true);
      } else {
        toast.error('Invoice URL not available for this sale.');
      }
    }
    setOpenDropdownId(null);
  };

  const handleSaleNotification = (sale) => {
    console.log('Sale notification:', sale);
    setSelectedSaleForNotification(sale);
    setShowNotificationModal(true);
    setOpenDropdownId(null);
  };

  const handleLedger = (sale) => {
    setSelectedSaleForLedger(sale);
    setShowLedgerModal(true);
    setOpenDropdownId(null);
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedSale(null);
  };

  // Handler functions for action buttons
  const handleAddPayment = (sale) => {
    console.log('Add payment:', sale);
    setSelectedSaleForPayment(sale);
    // Auto-fill due amount
    const dueAmount = sale.sellDue || '0.00';
    setPaymentData({
      method: 'Cash',
      paidOn: new Date().toISOString().slice(0, 16),
      amount: dueAmount,
      account: 'None',
      document: null,
      cardNumber: '',
      cardHolderName: '',
      cardExpiry: '',
      cardCvv: '',
      checkNo: '',
      checkExpiryDate: '',
      bankSubmissionDate: '',
      checkImage: null
    });
    setShowAddPaymentModal(true);
    setOpenDropdownId(null);
  };

  const handleDownloadEDI = (sale) => {
    toast.info('Download EDI feature coming soon');
  };

  const handlePackingSlip = async (sale) => {
    console.log('Packing slip sale data:', sale);
    const apiData = sale.apiData || sale;
    console.log('API data:', apiData);
    console.log('Sell lines:', apiData.sell_lines);
    console.log('Products:', sale.products);
    setOpenDropdownId(null);

    // Fetch business details from API
    let business = {};
    try {
      const bizResponse = await apiService.getBusinessDetails();
      console.log('Raw bizResponse:', JSON.stringify(bizResponse));
      // Handle nested data structures
      business = bizResponse?.data || bizResponse || {};
      // If business itself has a nested data property
      if (business.data && typeof business.data === 'object') {
        business = business.data;
      }
      console.log('Extracted business object:', JSON.stringify(business));
    } catch (err) {
      console.error('Failed to fetch business details:', err);
    }

    // Fetch locations to get location name
    let locations = [];
    let locationName = 'Main Branch';
    try {
      const locationsResponse = await apiService.getLocations();
      locations = locationsResponse.data || locationsResponse || [];
      const locationId = sale.locationId || apiData.location_id || sale.location;
      if (locationId) {
        const location = locations.find(loc => loc.id === parseInt(locationId));
        locationName = location ? location.name : `Location ${locationId}`;
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }

    // Fetch complete sale details if sell_lines are missing
    let completeSaleData = apiData;
    if (!apiData.sell_lines || apiData.sell_lines.length === 0) {
      try {
        const saleId = sale.id || apiData.id || apiData.transaction_id;
        if (saleId) {
          console.log('Fetching complete sale details for ID:', saleId);
          const saleDetails = await apiService.getSale(saleId);
          console.log('Fetched sale details:', saleDetails);

          // Handle response structure
          if (saleDetails.data && Array.isArray(saleDetails.data) && saleDetails.data.length > 0) {
            completeSaleData = saleDetails.data[0];
          } else if (saleDetails.data) {
            completeSaleData = saleDetails.data;
          } else {
            completeSaleData = saleDetails;
          }
          console.log('Complete sale data:', completeSaleData);
        }
      } catch (err) {
        console.error('Failed to fetch sale details:', err);
      }
    }

    const businessName = business.name || business.business_name || 'Business';
    const businessAddress = [
      business.address || business.address_line_1 || '',
      [business.city, business.state, business.zip || business.zip_code].filter(Boolean).join(', '),
      business.country || ''
    ].filter(Boolean).join(',\n');

    const businessPhone = business.phone || business.mobile || business.contact_no || business.alternate_number || '';
    const businessEmail = business.email || '';

    // Try multiple logo fields and URL patterns
    let logoUrl = '';
    const rawLogo = business.logo || business.logo_url || business.business_logo || business.image || '';
    console.log('Raw logo field:', rawLogo);
    if (rawLogo) {
      if (rawLogo.startsWith('http')) {
        logoUrl = rawLogo;
      } else {
        // Try the standard UltimatePOS storage path
        logoUrl = `https://et-pos.store/uploads/business_logos/${rawLogo}`;
      }
    }
    console.log('Final Logo URL:', logoUrl);

    const logoHtml = logoUrl
      ? `<img src="${logoUrl}" alt="Logo" style="max-height:80px;max-width:200px;display:block;margin-bottom:10px" onerror="this.style.display='none'">`
      : '';

    const invoiceNo = sale.invoiceNo || apiData.invoice_no || 'N/A';
    const packingSlipNo = `PS-${invoiceNo}`;

    const transactionDate = apiData.transaction_date || sale.saleDate;
    const formattedDate = transactionDate ? dateUtils.formatDateTime(transactionDate) : 'N/A';
    const shippingDate = apiData.shipping_date ? dateUtils.formatDate(apiData.shipping_date) : 'N/A';

    const storeName = locationName;

    const customerName = apiData.contact?.name || sale.customer || 'Walk-In Customer';
    const customerMobile = apiData.contact?.mobile || sale.contactNumber || '';
    const shippingAddress = apiData.shipping_address || apiData.contact?.address_line_1 || '';

    const packingNotes = apiData.additional_notes || sale.sellNote || 'None';
    const deliveryNotes = apiData.shipping_details || sale.shippingDetails || 'None';

    // Fetch product details for sell lines if product info is missing
    let sellLines = [];
    const rawSellLines = completeSaleData.sell_lines || apiData.sell_lines || sale.products || [];

    if (rawSellLines.length > 0) {
      sellLines = await Promise.all(rawSellLines.map(async (item, index) => {
        let productDetails = null;

        // If product details are missing, fetch them
        if (!item.product && item.product_id) {
          try {
            console.log(`Fetching product details for product_id: ${item.product_id}`);
            productDetails = await apiService.getProduct(item.product_id);
            console.log(`Fetched product details:`, productDetails);

            // Extract actual product from response (might be wrapped in data array)
            if (productDetails.data && Array.isArray(productDetails.data) && productDetails.data.length > 0) {
              productDetails = productDetails.data[0];
            } else if (productDetails.data && !Array.isArray(productDetails.data)) {
              productDetails = productDetails.data;
            }
            console.log(`Extracted product details:`, productDetails);
          } catch (err) {
            console.error(`Failed to fetch product ${item.product_id}:`, err);
          }
        }

        // Debug log to see what we're getting
        console.log(`Product item ${index}:`, item);
        console.log(`  - Available fields:`, Object.keys(item));
        console.log(`  - item.product:`, item.product);
        console.log(`  - fetched productDetails:`, productDetails);

        // Try multiple ways to get product name
        let productName = 'N/A';
        if (item.product?.name) productName = item.product.name;
        else if (item.name) productName = item.name;
        else if (item.product_name) productName = item.product_name;
        else if (item.variation?.name) productName = item.variation.name;
        else if (productDetails?.name) productName = productDetails.name;
        else productName = `Product #${item.product_id || item.id || item.variation_id || 'N/A'}`;

        // Try multiple ways to get SKU
        let sku = 'N/A';
        if (item.product?.sku) sku = item.product.sku;
        else if (item.sku) sku = item.sku;
        else if (item.product?.sku) sku = item.product.sku;
        else if (item.barcode) sku = item.barcode;
        else if (item.product?.barcode) sku = item.product.barcode;
        else if (item.sub_sku) sku = item.sub_sku;
        else if (productDetails?.sku) sku = productDetails.sku;
        else if (productDetails?.barcode) sku = productDetails.barcode;

        return {
          index: index + 1,
          productName: productName,
          productDescription: item.sell_line_note || item.description || item.product?.description || productDetails?.description || '',
          sku: sku,
          qty: parseFloat(item.quantity || item.qty || 0),
          unit: item.sub_unit?.short_name || item.unit?.short_name || item.product?.unit?.short_name || 'pcs'
        };
      }));
    }

    const totalItems = sellLines.length;
    const totalQuantity = sellLines.reduce((sum, item) => sum + item.qty, 0);

    const rows = sellLines.map(item => `
      <tr>
        <td class="text-center">${item.index}</td>
        <td>
          <strong>${item.productName}</strong>
          ${item.productDescription ? `<br><span style="font-size: 11px; color: #666; font-style: italic;">${item.productDescription}</span>` : ''}
        </td>
        <td>${item.sku}</td>
        <td class="text-center">${item.unit}</td>
        <td class="text-center" style="font-weight:bold; font-size:14px;">${item.qty.toFixed(2)}</td>
        <td class="text-center">
          <div style="width:100%; height:26px; border:2px solid #ccc; background:#fafafa; border-radius:3px;"></div>
        </td>
      </tr>
    `).join('');

    // Use hidden iframe — reliable same-page print without a new tab
    const iframeId = '__etpos_print_iframe';
    document.getElementById(iframeId)?.remove();

    const iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(iframe);

    const html = `<!DOCTYPE html><html><head>
      <meta charset="UTF-8">
      <title>Packing Slip - ${invoiceNo}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #2d3748; line-height: 1.5; font-size: 13px; background: #fff; padding: 20px; }
        .page-wrapper { max-width: 850px; margin: 0 auto; padding: 10px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; border-bottom: 3px solid #cbd5e0; padding-bottom: 20px; }
        .header-logo p { margin-bottom: 4px; font-size: 12px; color: #4a5568; }
        .header-title { text-align: right; }
        .header-title h1 { font-size: 32px; text-transform: uppercase; letter-spacing: 1px; color: #1a202c; margin-bottom: 8px; }
        
        .info-section { display: flex; justify-content: space-between; margin-bottom: 30px; gap: 20px; }
        .info-box { flex: 1; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px; background: #f8fafc; }
        .info-box h3 { font-size: 13px; text-transform: uppercase; background: #edf2f7; margin: -15px -15px 12px -15px; padding: 10px 15px; border-bottom: 1px solid #e2e8f0; border-radius: 6px 6px 0 0; color: #4a5568; letter-spacing: 0.5px; }
        .info-row { display: flex; margin-bottom: 6px; font-size: 13px; }
        .info-label { font-weight: bold; width: 130px; color: #4a5568; }
        .info-value { flex: 1; color: #1a202c; }

        table.product-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        table.product-table th { background: #edf2f7; text-transform: uppercase; font-size: 11px; font-weight: bold; padding: 12px 10px; text-align: left; border: 1px solid #e2e8f0; border-bottom: 2px solid #cbd5e0; color: #4a5568; letter-spacing: 0.5px; }
        table.product-table td { padding: 12px 10px; border: 1px solid #e2e8f0; font-size: 13px; vertical-align: middle; }
        table.product-table tr:nth-child(even) { background: #fdfdfd; }
        .text-center { text-align: center !important; }

        .summary-section { display: flex; justify-content: flex-end; margin-bottom: 30px; }
        .summary-box { width: 320px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px; background: #f8fafc; }
        .summary-row { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; font-weight: bold; border-bottom: 1px dashed #cbd5e0; padding-bottom: 8px; color: #2d3748; }
        .summary-row:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }

        .notes-section { display: flex; gap: 20px; margin-bottom: 40px; }
        .notes-box { flex: 1; border: 1px solid #e2e8f0; border-radius: 6px; padding: 15px; min-height: 90px; font-size: 13px; background: #fff; }
        .notes-box strong { display: block; margin-bottom: 8px; color: #4a5568; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px; border-bottom: 1px solid #edf2f7; padding-bottom: 5px; }

        .signature-section { display: flex; justify-content: space-between; margin-top: 50px; }
        .signature-box { flex: 1; margin: 0 20px; text-align: center; }
        .signature-line { border-bottom: 1.5px solid #a0aec0; height: 40px; margin-bottom: 12px; }
        .signature-box span { font-size: 12px; font-weight: bold; text-transform: uppercase; color: #4a5568; letter-spacing: 1px; }

        @media print {
          body { padding: 0; background: #fff; }
          .page-wrapper { width: 100%; max-width: 100%; padding: 0; }
          .info-box, .summary-box { background: #fff !important; border-color: #ccc; }
          .info-box h3, table.product-table th { background: #f5f5f5 !important; }
        }
      </style>
    </head>
    <body>
      <div class="page-wrapper">
        <div class="header">
          <div class="header-logo">
            ${logoHtml}
            <p style="font-weight:bold; font-size:18px; color:#1a202c; margin-bottom: 8px;">${businessName}</p>
            <p style="white-space: pre-line;">${businessAddress}</p>
            ${businessPhone ? `<p><strong>Phone:</strong> ${businessPhone}</p>` : ''}
            ${businessEmail ? `<p><strong>Email:</strong> ${businessEmail}</p>` : ''}
          </div>
          <div class="header-title">
            <h1>Packing Slip</h1>
            <p style="font-size:16px; color:#4a5568;">No. <strong style="color:#1a202c; font-size: 18px;">${packingSlipNo}</strong></p>
          </div>
        </div>

        <div class="info-section">
          <div class="info-box">
            <h3>Order Information</h3>
            <div class="info-row"><span class="info-label">Order/Invoice No:</span> <span class="info-value"><strong>${invoiceNo}</strong></span></div>
            <div class="info-row"><span class="info-label">Order Date:</span> <span class="info-value">${formattedDate}</span></div>
            <div class="info-row"><span class="info-label">Shipping Date:</span> <span class="info-value">${shippingDate}</span></div>
            <div class="info-row"><span class="info-label">Store/Branch:</span> <span class="info-value">${storeName}</span></div>
          </div>
          <div class="info-box">
            <h3>Customer & Shipping Info</h3>
            <div class="info-row"><span class="info-label">Customer Name:</span> <span class="info-value"><strong style="font-size: 14px;">${customerName}</strong></span></div>
            <div class="info-row"><span class="info-label">Contact No:</span> <span class="info-value">${customerMobile || '--'}</span></div>
            <div class="info-row"><span class="info-label">Shipping Addr:</span> <span class="info-value" style="white-space: pre-line;">${shippingAddress || '--'}</span></div>
          </div>
        </div>

        <table class="product-table">
          <thead>
            <tr>
              <th style="width: 5%;" class="text-center">#</th>
              <th style="width: 40%;">Product / Description</th>
              <th style="width: 15%;">SKU / Code</th>
              <th class="text-center" style="width: 10%;">Unit</th>
              <th class="text-center" style="width: 15%;">Ordered Qty</th>
              <th class="text-center" style="width: 15%;">Packed Qty</th>
            </tr>
          </thead>
          <tbody>
            ${rows || '<tr><td colspan="6" class="text-center" style="color:#a0aec0; padding: 30px;">No products found</td></tr>'}
          </tbody>
        </table>

        <div class="summary-section">
          <div class="summary-box">
            <div class="summary-row"><span>Total Items (Lines):</span> <span>${totalItems}</span></div>
            <div class="summary-row"><span>Total Quantity:</span> <span>${totalQuantity.toFixed(2)}</span></div>
            <div class="summary-row" style="margin-top: 15px; border: none; align-items: center;">
              <span>Total Packages/Boxes:</span> 
              <span style="display:inline-block; width: 70px; height: 30px; border: 2px solid #cbd5e0; background: #fff; border-radius: 4px;"></span>
            </div>
          </div>
        </div>

        <div class="notes-section">
          <div class="notes-box">
            <strong>📝 Special Packing Notes</strong>
            <p>${packingNotes}</p>
          </div>
          <div class="notes-box">
            <strong>🚚 Delivery Instructions</strong>
            <p>${deliveryNotes}</p>
          </div>
        </div>

        <div class="signature-section">
          <div class="signature-box">
            <div class="signature-line"></div>
            <span>Packed By</span>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <span>Checked By</span>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <span>Received By</span>
          </div>
        </div>
      </div>
    </body></html>`;

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => iframe.remove(), 1000);
      }, 300);
    };
  };
  const tabLabelMap = {
    all_sales: 'Sales',
    sales_returns: 'Sales Returns',
    shipment: 'Shipments',
    list_quotation: 'List Quotations',
    list_draft: 'Drafts',
    discount: 'Discounts'
  };

  const tabLabelSingularMap = {
    all_sales: 'sales',
    sales_returns: 'sales returns',
    shipment: 'shipments',
    list_quotation: 'quotations',
    list_draft: 'drafts',
    discount: 'discounts'
  };

  const activeTabLabel = tabLabelMap[activeTab] || 'Sales';
  const activeTabLabelSingular = tabLabelSingularMap[activeTab] || 'sales';

  const sortedSales = getSortedSales(filteredSales);

  const paginatedSales = sortedSales.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const totalPages = Math.ceil(filteredSales.length / entriesPerPage);

  return (
    <div className="sell-container">
      {successMessage && (
        <div className="sell-success-message">
          {successMessage}
        </div>
      )}
      {/* Main Content Card */}
      <div className="main-card">

        {/* Table Controls */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(true)}
                title="Open Filters"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { handleExport('CSV'); setShowExportDropdown(false); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { handleExport('Excel'); setShowExportDropdown(false); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { handlePrint(); setShowExportDropdown(false); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { handleColumnVisibility(); setShowExportDropdown(false); }}><i className="fa fa-columns"></i> Column Visibility</button>
                  <button onClick={() => { handleExport('PDF'); setShowExportDropdown(false); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            {activeTab === 'discount' ? (
              <button className="control-btn-primary" onClick={() => setShowDiscountModal(true)}>
                <span className="plus-icon">+</span> Add
              </button>
            ) : activeTab === 'all_sales' || activeTab === 'pos_sales' ? (
              <button className="control-btn-primary" onClick={() => navigate('/sell/add')}>
                <span className="plus-icon">+</span> Add
              </button>
            ) : activeTab === 'list_draft' ? (
              <button className="control-btn-primary" onClick={() => navigate('/sell/add-draft')}>
                <span className="plus-icon">+</span> Add
              </button>
            ) : activeTab === 'list_quotation' ? (
              <button className="control-btn-primary" onClick={() => navigate('/sell/add-quotation')}>
                <span className="plus-icon">+</span> Add
              </button>
            ) : null}
          </div>
        </div>

        {/* Discount Tab Content */}
        {activeTab === 'discount' && (
          <div className="products-table-container">
            {discounts.length === 0 ? (
              <div className="no-sales">
                <h3>🏷️ No Discounts Found</h3>
                <p>No discounts have been added yet. Click "Add" to create a new discount.</p>
              </div>
            ) : (
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Discount Type</th>
                    <th>Discount Amount</th>
                    <th>Starts At</th>
                    <th>Ends At</th>
                    <th>Location</th>
                    <th>Priority</th>
                    <th>Active</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((d, i) => (
                    <tr key={i}>
                      <td>{d.name}</td>
                      <td>{d.discountType}</td>
                      <td>{d.discountAmount}</td>
                      <td>{d.startsAt || '—'}</td>
                      <td>{d.endsAt || '—'}</td>
                      <td>{d.location || '—'}</td>
                      <td>{d.priority || '—'}</td>
                      <td>
                        <span className={`payment-status-badge ${d.isActive ? 'paid' : 'due'}`}>
                          {d.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="control-btn-primary"
                          style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                          onClick={() => {
                            setDiscounts(prev => prev.filter((_, idx) => idx !== i));
                            toast.success('Discount deleted successfully!');
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}


        {/* Sales Table */}
        <div className="products-table-container" style={{ display: (activeTab === 'discount') ? 'none' : undefined }}>
          {
            isLoadingSales ? (
              <div className="loading-sales" >
                <h3>⏳ Loading {activeTabLabel}...</h3>
                <p>Fetching {activeTabLabelSingular} data from server...</p>
              </div>
            ) : salesError ? (
              <div className="error-sales">
                <h3>❌ Error Loading {activeTabLabel}</h3>
                <p>{salesError}</p>
                <button onClick={() => window.location.reload()} className="retry-btn">
                  🔄 Retry
                </button>
              </div>
            ) : filteredSales.length === 0 ? (
              <div className="no-sales">
                <h3>📊 No {activeTabLabel} Found</h3>
                <p>
                  {sales.length === 0
                    ? `No ${activeTabLabelSingular} have been recorded yet.`
                    : `No ${activeTabLabelSingular} match your current filters.`
                  }
                </p>
              </div>
            ) : (
              <>
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      {((activeTab === 'list_quotation' || activeTab === 'list_draft') ? [
                        { key: 'saleDate', label: 'Date' },
                        { key: 'invoiceNo', label: 'Reference No' },
                        { key: 'customer', label: 'Customer name' },
                        { key: 'contactNumber', label: 'Contact Number' },
                        { key: 'location', label: 'Location' },
                        { key: 'totalItems', label: 'Total Items' },
                        { key: 'addedBy', label: 'Added By' }
                      ] : activeTab === 'shipment' ? [
                        { key: 'saleDate', label: 'Date' },
                        { key: 'invoiceNo', label: 'Invoice No.' },
                        { key: 'customer', label: 'Customer name' },
                        { key: 'contactNumber', label: 'Contact Number' },
                        { key: 'location', label: 'Location' },
                        { key: 'deliveryPerson', label: 'Delivery Person' },
                        { key: 'shippingStatus', label: 'Shipping Status' },
                        { key: 'paymentStatus', label: 'Payment Status' },
                        { key: 'serviceStaff', label: 'Service staff' }
                      ] : activeTab !== 'sales_returns' ? [
                        { key: 'saleDate', label: 'Date' },
                        { key: 'invoiceNo', label: 'Invoice No.' },
                        { key: 'customer', label: 'Customer name' },
                        { key: 'contactNumber', label: 'Contact Number' },
                        { key: 'location', label: 'Location' },
                        { key: 'paymentStatus', label: 'Payment Status' },
                        { key: 'paymentMethod', label: 'Payment Method' },
                        { key: 'totalPayable', label: 'Total amount' },
                        { key: 'totalPaid', label: 'Total paid' },
                        { key: 'sellDue', label: 'Sell Due' },
                        { key: 'sellRetDue', label: 'Sell Ret Due' },
                        { key: 'shippingStatus', label: 'Shipping Status' },
                        { key: 'createdBy', label: 'Created By' },
                        { key: 'businessLocation', label: 'Business Location' },
                        { key: 'commissionAgent', label: 'Commission Agent' },
                        { key: 'taxAmount', label: 'Tax Amount' },
                        { key: 'discountAmount', label: 'Discount Amount' },
                        { key: 'shippingCharges', label: 'Shipping Charges' },
                        { key: 'finalTotal', label: 'Final Total' },
                        { key: 'createdAt', label: 'Added At' },
                        { key: 'updatedAt', label: 'Updated At' }
                      ] : [
                        { key: 'saleDate', label: 'Date' },
                        { key: 'invoiceNo', label: 'Invoice No.' },
                        { key: 'parentSale', label: 'Parent Sale' },
                        { key: 'customer', label: 'Customer name' },
                        { key: 'location', label: 'Location' },
                        { key: 'paymentStatus', label: 'Payment Status' },
                        { key: 'totalPayable', label: 'Return Amount' },
                        { key: 'totalPaid', label: 'Return Paid' },
                        { key: 'sellDue', label: 'Return Due' }
                      ]).map(({ key, label }) => (
                        <th key={key} className="sortable" onClick={() => handleSort(key)}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                            {label}
                            {sortConfig.key === key ? (
                              sortConfig.direction === 'ascending' ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 15l-6-6-6 6" />
                                </svg>
                              ) : (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M6 9l6 6 6-6" />
                                </svg>
                              )
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M7 15l5 5 5-5" />
                                <path d="M7 9l5-5 5 5" />
                              </svg>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSales.map((sale) => (
                      <tr key={sale.id}>
                        <td className="actions-cell">
                          <div className="action-dropdown-container">
                            <button
                              className="action-btn-trigger"
                              onClick={(e) => toggleDropdown(sale.id, e)}
                            >
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openDropdownId === sale.id && ReactDOM.createPortal(
                              <div
                                className="action-dropdown-menu"
                                style={{
                                  position: 'fixed',
                                  top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                  bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                  left: `${dropdownPosition.left}px`,
                                  maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                  zIndex: 9999999
                                }}
                              >
                                {(activeTab === 'list_draft' || activeTab === 'list_quotation') ? (
                                  <>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleViewSale(sale);
                                      }}
                                    >
                                      👁️ View
                                    </button>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditSale(sale);
                                      }}
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePrintInvoice(sale);
                                      }}
                                    >
                                      🖨️ Print
                                    </button>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleConvertToProforma(sale);
                                      }}
                                    >
                                      🔄 Convert to Proforma Invoice
                                    </button>
                                    <button
                                      className="dropdown-item delete-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSale(sale.id);
                                      }}
                                    >
                                      🗑️ Delete
                                    </button>
                                    {activeTab === 'list_quotation' && (
                                      <>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyQuotation(sale);
                                          }}
                                        >
                                          📋 Copy Quotation
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaleNotification(sale);
                                          }}
                                        >
                                          ✉️ New quotation notification
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleInvoiceUrl(sale.id);
                                          }}
                                        >
                                          🔗 View quote url
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : activeTab === 'shipment' ? (
                                  <>
                                    <button className="dropdown-item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewSale(sale); }}>
                                      👁️ View
                                    </button>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleEditShipping(sale); }}>
                                      🚚 Edit Shipping
                                    </button>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handlePrintInvoice(sale); }}>
                                      🖨️ Print Invoice
                                    </button>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handlePackingSlip(sale); }}>
                                      📄 Packing Slip
                                    </button>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleDeliveryNote(sale); }}>
                                      📄 Delivery Note
                                    </button>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleDownloadEDI(sale); }}>
                                      📥 Download EDI
                                    </button>
                                    <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleSaleNotification(sale); }}>
                                      ✉️ New Sale Notification
                                    </button>
                                    <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleLedger(sale); }}>
                                      📊 Ledger
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleViewSale(sale);
                                      }}
                                    >
                                      👁️ View
                                    </button>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditSale(sale);
                                      }}
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      className="dropdown-item delete-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSale(sale.id);
                                      }}
                                    >
                                      🗑️ Delete
                                    </button>
                                    {activeTab !== 'sales_returns' && (
                                      <>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditShipping(sale);
                                          }}
                                        >
                                          🚚 Edit Shipping
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handlePrintInvoice(sale);
                                          }}
                                        >
                                          🖨️ Print Invoice
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewPayments(sale);
                                          }}
                                        >
                                          💰 View Payments
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddPayment(sale);
                                          }}
                                        >
                                          💳 Add Payment
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSellReturn(sale);
                                          }}
                                        >
                                          ↩️ Sell Return
                                        </button>
                                      </>
                                    )}
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleInvoiceUrl(sale.id);
                                      }}
                                    >
                                      🔗 Invoice URL
                                    </button>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaleNotification(sale);
                                      }}
                                    >
                                      ✉️ New Sale Notification
                                    </button>
                                    <button
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLedger(sale);
                                      }}
                                    >
                                      📊 Ledger
                                    </button>
                                  </>
                                )}
                              </div>,
                              document.body
                            )}
                          </div>
                        </td>
                        <td className="date-cell">
                          <div>{(sale.saleDate || '').substring(0, 10) || 'N/A'}</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                            {(sale.saleDate || '').substring(11, 16) || ''}
                          </div>
                        </td>
                        <td className="invoice-cell">
                          {sale.invoiceNo || 'N/A'}
                          {sale.apiData?.sub_status === 'proforma' && (
                            <div style={{ marginTop: '4px' }}>
                              <span style={{ backgroundColor: '#17a2b8', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>Proforma Invoice</span>
                            </div>
                          )}
                        </td>
                        {activeTab === 'sales_returns' && (
                          <td className="parent-sale-cell">{sale.apiData?.return_parent_sell?.invoice_no || sale.apiData?.return_parent?.invoice_no || '--'}</td>
                        )}
                        <td className="customer-cell">{sale.customer || 'N/A'}</td>
                        {activeTab !== 'sales_returns' && (
                          <td className="contact-cell">{sale.contactNumber || 'N/A'}</td>
                        )}
                        <td className="location-cell">
                          {
                            sale.apiData?.location?.name ||
                            businessLocations.find(l => l.id === parseInt(sale.apiData?.location_id))?.name ||
                            sale.location || 'N/A'
                          }
                        </td>
                        {activeTab !== 'list_quotation' && activeTab !== 'list_draft' && activeTab !== 'shipment' && (
                          <>
                            <td className="payment-status-cell">
                              <span className={`payment-status-badge ${sale.paymentStatus || 'due'}`}>
                                {sale.paymentStatus ? sale.paymentStatus.charAt(0).toUpperCase() + sale.paymentStatus.slice(1) : 'Due'}
                              </span>
                            </td>
                            {activeTab !== 'sales_returns' && (
                              <td className="payment-method-cell">{sale.paymentMethod || 'N/A'}</td>
                            )}
                            <td className="amount-cell">{formatCurrency(sale.totalPayable)}</td>
                            <td className="paid-cell highlight">{formatCurrency(sale.totalPaid || '0.00')}</td>
                            <td className="due-cell">{formatCurrency(sale.sellDue || '0.00')}</td>
                          </>
                        )}

                        {(activeTab === 'list_quotation' || activeTab === 'list_draft') ? (
                          <>
                            <td className="total-items-cell">{sale.totalItems || '0.00'}</td>
                            <td className="added-by-cell">{sale.addedBy || 'N/A'}</td>
                          </>
                        ) : activeTab === 'shipment' ? (
                          <>
                            <td className="delivery-person-cell">{sale.deliveryPerson || sale.apiData?.delivery_person || 'N/A'}</td>
                            <td className="shipping-status-cell">
                              <span className={`shipping-status-badge ${sale.shippingStatus || 'pending'}`}>
                                {sale.shippingStatus || 'Pending'}
                              </span>
                            </td>
                            <td className="payment-status-cell">
                              <span className={`payment-status-badge ${sale.paymentStatus || 'due'}`}>
                                {sale.paymentStatus ? sale.paymentStatus.charAt(0).toUpperCase() + sale.paymentStatus.slice(1) : 'Due'}
                              </span>
                            </td>
                            <td className="service-staff-cell">{sale.serviceStaff || sale.apiData?.service_staff || 'N/A'}</td>
                          </>
                        ) : activeTab !== 'sales_returns' ? (
                          <>
                            <td className="ret-due-cell">{formatCurrency(sale.sellRetDue || '0.00')}</td>
                            <td className="shipping-status-cell">
                              <span className={`shipping-status-badge ${sale.shippingStatus || 'pending'}`}>
                                {sale.shippingStatus || 'Pending'}
                              </span>
                            </td>
                            <td className="created-by-cell">
                              {(() => {
                                const createdBy = sale.apiData?.created_by || sale.created_by;
                                if (!createdBy) return 'N/A';
                                const user = users.find(u => u.id === parseInt(createdBy));
                                if (user) {
                                  return user.first_name && user.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.username || user.name || `User ${createdBy}`;
                                }
                                return `User ${createdBy}`;
                              })()}
                            </td>
                            <td className="business-location-cell">
                              {(() => {
                                const businessId = sale.apiData?.location_id || sale.business_id;
                                if (!businessId) return 'N/A';
                                const location = businessLocations.find(l => l.id === parseInt(businessId));
                                return location ? location.name : `Location ${businessId}`;
                              })()}
                            </td>
                            <td className="commission-agent-cell">{sale.commissionAgent || 'N/A'}</td>
                            <td className="tax-amount-cell">{formatCurrency(sale.taxAmount || '0.00')}</td>
                            <td className="discount-amount-cell">{formatCurrency(sale.discountAmount || '0.00')}</td>
                            <td className="shipping-charges-cell">{formatCurrency(sale.shippingCharges || '0.00')}</td>
                            <td className="final-total-cell">{formatCurrency(sale.finalTotal || sale.totalPayable)}</td>
                            <td className="added-at-cell">{formatDate(sale.createdAt)}</td>
                            <td className="updated-at-cell">{formatDate(sale.updatedAt) || formatDate(sale.createdAt)}</td>
                          </>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                  <div>
                    Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, filteredSales.length)} of {filteredSales.length} entries
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
        </div>
      </div>

      {/* Add Discount Modal */}
      {showDiscountModal && (
        <div className="modal-overlay" onClick={() => setShowDiscountModal(false)}>
          <div
            className="modal-content"
            style={{
              background: 'white',
              borderRadius: '1rem',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              width: '95%',
              maxWidth: '760px',
              maxHeight: '90vh',
              overflowY: 'auto',
              animation: 'modalSlideIn 0.3s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="modal-header">
              <h3>Add Discount</h3>
              <button className="modal-close-btn" onClick={() => setShowDiscountModal(false)}>×</button>
            </div>

            {/* Body */}
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                {/* Name */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
                    Name: <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Name"
                    value={discountForm.name}
                    onChange={(e) => setDiscountForm(f => ({ ...f, name: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Products */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Products:</label>
                  <select
                    multiple
                    value={discountForm.products}
                    onChange={(e) => setDiscountForm(f => ({ ...f, products: Array.from(e.target.selectedOptions, o => o.value) }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', minHeight: '80px', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Select Products --</option>
                  </select>
                </div>

                {/* Brand */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Brand:</label>
                  <select
                    value={discountForm.brand}
                    onChange={(e) => setDiscountForm(f => ({ ...f, brand: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Select Brand --</option>
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Category:</label>
                  <select
                    value={discountForm.category}
                    onChange={(e) => setDiscountForm(f => ({ ...f, category: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Select Category --</option>
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
                    Location: <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={discountForm.location}
                    onChange={(e) => setDiscountForm(f => ({ ...f, location: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Select Location --</option>
                    {businessLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Priority:</label>
                  <input
                    type="number"
                    placeholder="Priority"
                    value={discountForm.priority}
                    onChange={(e) => setDiscountForm(f => ({ ...f, priority: e.target.value }))}
                    min="0"
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Discount Type */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
                    Discount Type: <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={discountForm.discountType}
                    onChange={(e) => setDiscountForm(f => ({ ...f, discountType: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Select Discount Type --</option>
                    <option value="Percentage">Percentage (%)</option>
                    <option value="Fixed">Fixed ($)</option>
                  </select>
                </div>

                {/* Discount Amount */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
                    Discount Amount: <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Discount Amount"
                    value={discountForm.discountAmount}
                    onChange={(e) => setDiscountForm(f => ({ ...f, discountAmount: e.target.value }))}
                    min="0"
                    step="0.01"
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Starts At */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Starts At:</label>
                  <input
                    type="datetime-local"
                    placeholder="Starts At"
                    value={discountForm.startsAt}
                    onChange={(e) => setDiscountForm(f => ({ ...f, startsAt: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Ends At */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Ends At:</label>
                  <input
                    type="datetime-local"
                    placeholder="Ends At"
                    value={discountForm.endsAt}
                    onChange={(e) => setDiscountForm(f => ({ ...f, endsAt: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Selling Price Group */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', display: 'block', marginBottom: '0.4rem' }}>Selling Price Group:</label>
                  <select
                    value={discountForm.sellingPriceGroup}
                    onChange={(e) => setDiscountForm(f => ({ ...f, sellingPriceGroup: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  >
                    <option value="All">All</option>
                  </select>
                </div>

                {/* Apply in Customer Groups */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.25rem' }}>
                  <input
                    type="checkbox"
                    id="applyCustomerGroups"
                    checked={discountForm.applyInCustomerGroups}
                    onChange={(e) => setDiscountForm(f => ({ ...f, applyInCustomerGroups: e.target.checked }))}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="applyCustomerGroups" style={{ fontWeight: 500, fontSize: '0.9rem', color: '#374151', cursor: 'pointer', margin: 0 }}>
                    Apply in customer groups
                  </label>
                </div>

                {/* Is Active */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={discountForm.isActive}
                    onChange={(e) => setDiscountForm(f => ({ ...f, isActive: e.target.checked }))}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="isActive" style={{ fontWeight: 500, fontSize: '0.9rem', color: '#374151', cursor: 'pointer', margin: 0 }}>
                    Is active
                  </label>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountForm({ name: '', products: [], brand: '', category: '', location: '', priority: '', discountType: '', discountAmount: '', startsAt: '', endsAt: '', sellingPriceGroup: 'All', applyInCustomerGroups: false, isActive: false });
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  if (!discountForm.name.trim()) { toast.warning('Name is required.'); return; }
                  if (!discountForm.location) { toast.warning('Location is required.'); return; }
                  if (!discountForm.discountType) { toast.warning('Discount Type is required.'); return; }
                  if (!discountForm.discountAmount) { toast.warning('Discount Amount is required.'); return; }
                  setDiscounts(prev => [...prev, { ...discountForm }]);
                  toast.success('Discount added successfully!');
                  setShowDiscountModal(false);
                  setDiscountForm({ name: '', products: [], brand: '', category: '', location: '', priority: '', discountType: '', discountAmount: '', startsAt: '', endsAt: '', sellingPriceGroup: 'All', applyInCustomerGroups: false, isActive: false });
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Sale Modal */}
      {showViewModal && (
        <ViewSaleModal
          sale={selectedSale}
          onClose={handleCloseModal}
          onPrintInvoice={handlePrintInvoice}
          onPackingSlip={handlePackingSlip}
        />
      )}

      {/* Invoice URL Modal */}
      {showInvoiceUrlModal && (
        <div className="modal-overlay" onClick={() => setShowInvoiceUrlModal(false)}>
          <div className="modal-content invoice-url-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invoice URL</h3>
              <button className="modal-close-btn" onClick={() => setShowInvoiceUrlModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="invoice-url-section">
                <input
                  type="text"
                  value={invoiceUrlToShow}
                  readOnly
                  className="invoice-url-input"
                />
                <p className="url-help-text">Link to view the invoice without login.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowInvoiceUrlModal(false)}>
                Close
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  navigator.clipboard.writeText(invoiceUrlToShow);
                  toast.success('Invoice URL copied to clipboard!');
                }}
              >
                📋 Copy
              </button>
              <button
                className="btn-primary"
                onClick={() => window.open(invoiceUrlToShow, '_blank')}
              >
                🔗 View
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Payment Modal */}
      {showAddPaymentModal && selectedSaleForPayment && (
        <div className="modal-overlay payment-modal-overlay" onClick={() => setShowAddPaymentModal(false)}>
          <div className="modal-content payment-modal dark-mode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Payment</h3>
              <button className="modal-close-btn" onClick={() => setShowAddPaymentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Info Cards */}
              <div className="payment-info-cards">
                <div className="info-card">
                  <label>Customer :</label>
                  <span>{selectedSaleForPayment.customer || 'Business'}</span>
                </div>
                <div className="info-card">
                  <label>Invoice No.:</label>
                  <span>{selectedSaleForPayment.invoiceNo || 'N/A'}</span>
                  <label>Location:</label>
                  <span>
                    {(() => {
                      console.log('Add Payment - selectedSaleForPayment:', selectedSaleForPayment);
                      console.log('Add Payment - businessLocations:', businessLocations);

                      // Extract location ID from location field (e.g., "Location 49" -> 49)
                      const locationField = selectedSaleForPayment.location;
                      console.log('Add Payment - locationField:', locationField);

                      if (!locationField) return 'N/A';

                      // Extract number from "Location 49" format
                      const locationMatch = locationField.match(/Location (\d+)/);
                      const locationId = locationMatch ? parseInt(locationMatch[1]) : null;
                      console.log('Add Payment - extracted locationId:', locationId);

                      if (!locationId) return locationField; // Return original if can't extract

                      const location = businessLocations.find(l => l.id === locationId);
                      console.log('Add Payment - found location:', location);
                      return location ? location.name : locationField;
                    })()}
                  </span>
                </div>
                <div className="info-card">
                  <label>Total amount:</label>
                  <span>${selectedSaleForPayment.finalTotal || selectedSaleForPayment.totalPayable || selectedSaleForPayment.totalAmount || '0.00'}</span>
                  <label>Payment Note:</label>
                  <span>--</span>
                </div>
              </div>

              {/* Advance Balance */}
              <div className="advance-balance">
                <span>Advance Balance: $ 0.00</span>
              </div>

              {/* Payment Form */}
              <div className="payment-form">
                {/* Row 1: Payment Method + Paid On */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Payment Method:*</label>
                    <select
                      value={paymentData.method}
                      onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                    >
                      <option value="Cash">💵 Cash</option>
                      <option value="Card">💳 Card</option>
                      <option value="Bank Transfer">🏦 Bank Transfer</option>
                      <option value="Cheque">📝 Cheque</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Paid on:*</label>
                    <input
                      type="datetime-local"
                      value={paymentData.paidOn}
                      onChange={(e) => setPaymentData({ ...paymentData, paidOn: e.target.value })}
                    />
                  </div>
                </div>

                {/* Row 2: Amount + Payment Account */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount:*</label>
                    <div className="amount-input-wrapper">
                      <span className="currency-icon">💵</span>
                      <input
                        type="number"
                        value={paymentData.amount}
                        onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Payment Account:</label>
                    <select
                      value={paymentData.account}
                      onChange={(e) => setPaymentData({ ...paymentData, account: e.target.value })}
                      disabled={isLoadingPaymentAccounts}
                    >
                      <option value="None">None</option>
                      {isLoadingPaymentAccounts ? (
                        <option value="" disabled>Loading payment accounts...</option>
                      ) : (
                        paymentAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name || account.account_name || account.display_name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Card Payment Fields */}
                {paymentData.method === 'Card' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Card Number:</label>
                      <input
                        type="text"
                        value={paymentData.cardNumber}
                        onChange={(e) => setPaymentData({ ...paymentData, cardNumber: e.target.value })}
                        placeholder="1234 5678 9012 3456"
                        maxLength="19"
                      />
                    </div>
                    <div className="form-group">
                      <label>Card Holder Name:</label>
                      <input
                        type="text"
                        value={paymentData.cardHolderName}
                        onChange={(e) => setPaymentData({ ...paymentData, cardHolderName: e.target.value })}
                        placeholder="John Doe"
                      />
                    </div>
                  </div>
                )}
                {paymentData.method === 'Card' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Card Expiry:</label>
                      <input
                        type="text"
                        value={paymentData.cardExpiry}
                        onChange={(e) => setPaymentData({ ...paymentData, cardExpiry: e.target.value })}
                        placeholder="MM/YY"
                        maxLength="5"
                      />
                    </div>
                    <div className="form-group">
                      <label>CVV:</label>
                      <input
                        type="text"
                        value={paymentData.cardCvv}
                        onChange={(e) => setPaymentData({ ...paymentData, cardCvv: e.target.value })}
                        placeholder="123"
                        maxLength="4"
                      />
                    </div>
                  </div>
                )}

                {/* Check/Cheque Payment Fields */}
                {(paymentData.method === 'Cheque' || paymentData.method === 'Check') && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Check No.:</label>
                      <input
                        type="text"
                        value={paymentData.checkNo}
                        onChange={(e) => setPaymentData({ ...paymentData, checkNo: e.target.value })}
                        placeholder="Check No."
                      />
                    </div>
                    <div className="form-group">
                      <label>Check Expiry Date:</label>
                      <input
                        type="date"
                        value={paymentData.checkExpiryDate}
                        onChange={(e) => setPaymentData({ ...paymentData, checkExpiryDate: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                {(paymentData.method === 'Cheque' || paymentData.method === 'Check') && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Bank Submission Date:</label>
                      <input
                        type="date"
                        value={paymentData.bankSubmissionDate}
                        onChange={(e) => setPaymentData({ ...paymentData, bankSubmissionDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Check Image:</label>
                      <input
                        type="file"
                        onChange={(e) => setPaymentData({ ...paymentData, checkImage: e.target.files[0] })}
                        accept="image/jpeg, image/png, application/pdf"
                      />
                    </div>
                  </div>
                )}

                {/* Row 3: Attach Document - full width */}
                <div className="form-row" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="form-group file-group">
                    <label>Attach Document:</label>
                    <input
                      type="file"
                      onChange={(e) => setPaymentData({ ...paymentData, document: e.target.files[0] })}
                      accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png"
                    />
                    <small>Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddPaymentModal(false)}>
                Close
              </button>
              <button
                className="btn-primary add-payment-btn"
                onClick={async () => {
                  try {
                    console.log('Payment data:', paymentData);
                    console.log('Selected sale:', selectedSaleForPayment);

                    // Validate required fields
                    if (!selectedSaleForPayment?.id) {
                      toast.error('Transaction ID is required');
                      return;
                    }

                    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
                      toast.warning('Please enter a valid payment amount');
                      return;
                    }

                    // Format date to Y-m-d H:i:s
                    const formatDateTime = (dateTimeStr) => {
                      if (!dateTimeStr) return null;
                      const date = new Date(dateTimeStr);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                    };

                    // Map payment method names to API format
                    const methodMap = {
                      'Cash': 'cash',
                      'Card': 'card',
                      'Cheque': 'cheque',
                      'Check': 'cheque',
                      'Bank Transfer': 'bank_transfer',
                      'Credit': 'credit',
                      'Other': 'other'
                    };

                    const paymentMethod = methodMap[paymentData.method] || paymentData.method.toLowerCase();

                    // Build API payload
                    const apiPayload = {
                      transaction_id: parseInt(selectedSaleForPayment.id),
                      payment_method: paymentMethod,
                      amount: parseFloat(paymentData.amount),
                      paid_on: formatDateTime(paymentData.paidOn),
                      payment_account_id: paymentData.account || paymentMethod,
                      payment_note: paymentData.note || null
                    };

                    // Add card-specific fields
                    if (paymentMethod === 'card' && paymentData.cardNumber) {
                      apiPayload.card_number = paymentData.cardNumber;
                      apiPayload.card_holder_name = paymentData.cardHolderName || '';
                      apiPayload.card_type = 'visa'; // Default, could be made dynamic

                      // Parse MM/YY format
                      if (paymentData.cardExpiry) {
                        const [month, year] = paymentData.cardExpiry.split('/');
                        if (month) apiPayload.card_month = month;
                        if (year) apiPayload.card_year = '20' + year; // Convert YY to YYYY
                      }

                      if (paymentData.cardCvv) apiPayload.card_security = paymentData.cardCvv;
                    }

                    // Add cheque-specific fields
                    if (paymentMethod === 'cheque') {
                      if (paymentData.checkNo) apiPayload.cheque_number = paymentData.checkNo;
                      if (paymentData.checkExpiryDate) apiPayload.cheque_expiry_date = paymentData.checkExpiryDate;
                      if (paymentData.bankSubmissionDate) apiPayload.bank_submission_date = paymentData.bankSubmissionDate;
                    }

                    console.log('Submitting payment to API:', JSON.stringify(apiPayload, null, 2));

                    // Call API
                    const response = await apiService.addPayment(apiPayload);

                    console.log('Payment added successfully:', response);

                    // Show success message
                    toast.success('Payment added successfully!');

                    // Refresh sales list
                    loadSales();

                    // Close modal
                    setShowAddPaymentModal(false);

                  } catch (error) {
                    console.error('Error adding payment:', error);
                    toast.error('Failed to add payment: ' + (error.message || 'Unknown error'));
                  }
                }}
              >
                ➕ Add Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Payment Modal */}
      {showViewPaymentModal && selectedSaleForViewPayment && (
        <div className="modal-overlay payment-history-overlay" onClick={() => setShowViewPaymentModal(false)}>
          <div className="modal-content view-payment-modal dark-mode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment History - Invoice #{selectedSaleForViewPayment.invoiceNo || 'N/A'}</h3>
              <button className="modal-close-btn" onClick={() => setShowViewPaymentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Sale Info */}
              <div className="view-payment-info">
                <div className="info-row">
                  <span className="info-label">Customer:</span>
                  <span className="info-value">{selectedSaleForViewPayment.customer || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Location:</span>
                  <span className="info-value">
                    {
                      selectedSaleForViewPayment.apiData?.location?.name ||
                      businessLocations.find(l => l.id === parseInt(selectedSaleForViewPayment.apiData?.location_id))?.name ||
                      selectedSaleForViewPayment.location || 'N/A'
                    }
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Total Payable:</span>
                  <span className="info-value">${parseFloat(selectedSaleForViewPayment.totalPayable || selectedSaleForViewPayment.finalTotal || 0).toFixed(2)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Total Paid:</span>
                  <span className="info-value">${parseFloat(selectedSaleForViewPayment.totalPaid || 0).toFixed(2)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payment Status:</span>
                  <span className={`status-badge payment-status-badge ${selectedSaleForViewPayment.paymentStatus?.toLowerCase() || 'due'}`}>
                    {selectedSaleForViewPayment.paymentStatus || 'Due'}
                  </span>
                </div>
              </div>

              {/* Payment Table */}
              <div className="payment-table-container">
                <table className="payment-history-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Reference No</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Note</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const payments = selectedSaleForViewPayment.apiData?.payment_lines || [];
                      if (payments.length === 0) {
                        return (
                          <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>
                              No payment records found.
                            </td>
                          </tr>
                        );
                      }
                      return payments.map((p, i) => {
                        const isEditing = editingPayment === p.id;
                        if (isEditing) {
                          return (
                            <tr key={p.id || i} style={{ background: '#f0fdf4' }}>
                              <td>{i + 1}</td>
                              <td>{p.paid_on ? (s => `${s.slice(5,7)}/${s.slice(8,10)}/${s.slice(0,4)}`)(p.paid_on) : '—'}</td>
                              <td>{p.payment_ref_no || '—'}</td>
                              <td>
                                <input
                                  type="number"
                                  value={editPaymentData.amount ?? ''}
                                  onChange={e => setEditPaymentData(d => ({ ...d, amount: e.target.value }))}
                                  style={{ width: '90px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                />
                              </td>
                              <td>
                                <select
                                  value={editPaymentData.method ?? ''}
                                  onChange={e => setEditPaymentData(d => ({ ...d, method: e.target.value }))}
                                  style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                >
                                  <option value="Cash">Cash</option>
                                  <option value="Card">Card</option>
                                  <option value="Bank Transfer">Bank Transfer</option>
                                  <option value="Cheque">Cheque</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editPaymentData.note ?? ''}
                                  onChange={e => setEditPaymentData(d => ({ ...d, note: e.target.value }))}
                                  placeholder="Note"
                                  style={{ width: '100px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                />
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button
                                  className="pay-action-btn pay-save-btn"
                                  title="Save"
                                  onClick={async () => {
                                    try {
                                      await apiService.updateSellPayment(p.id, {
                                        amount: parseFloat(editPaymentData.amount),
                                        method: editPaymentData.method,
                                        note: editPaymentData.note,
                                      });
                                      setEditingPayment(null);
                                      await loadSales();
                                      toast.success('Payment updated successfully!');
                                    } catch (err) {
                                      toast.error('Failed to update payment: ' + err.message);
                                    }
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                  Save
                                </button>
                                <button
                                  className="pay-action-btn pay-cancel-btn"
                                  title="Cancel"
                                  onClick={() => setEditingPayment(null)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                  Cancel
                                </button>
                              </td>
                            </tr>
                          );
                        }
                        return (
                          <tr key={p.id || i}>
                            <td>{i + 1}</td>
                            <td>{p.paid_on ? (s => `${s.slice(5,7)}/${s.slice(8,10)}/${s.slice(0,4)}`)(p.paid_on) : '—'}</td>
                            <td>{p.payment_ref_no || '—'}</td>
                            <td className="payment-amount-cell">${parseFloat(p.amount || 0).toFixed(2)}</td>
                            <td>{p.method || '—'}</td>
                            <td>{p.note || '—'}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <div className="pay-action-group">
                                {/* Edit Button */}
                                <button
                                  className="pay-action-btn pay-edit-btn"
                                  title="Edit Payment"
                                  onClick={() => {
                                    setEditingPayment(p.id);
                                    setEditPaymentData({ amount: p.amount, method: p.method || 'Cash', note: p.note || '' });
                                  }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                  Edit
                                </button>
                                {/* Delete Button */}
                                <button
                                  className="pay-action-btn pay-delete-btn"
                                  title="Delete Payment"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this payment?')) {
                                      apiService.deleteSellPayment?.(p.id)
                                        .then(() => {
                                          toast.success('Payment deleted successfully!');
                                          loadSales();
                                          setShowViewPaymentModal(false);
                                        })
                                        .catch(err => {
                                          toast.error('Delete failed: ' + (err.message || 'Unknown error'));
                                        });
                                    }
                                  }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4h6v2"></path></svg>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowViewPaymentModal(false)}>
                Close
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  const sale = selectedSaleForViewPayment;
                  const payments = sale.apiData?.payment_lines || [];
                  const rows = payments.map((p, i) => `
                    <tr>
                      <td>${i + 1}</td>
                      <td>${p.paid_on ? (s => `${s.slice(5,7)}/${s.slice(8,10)}/${s.slice(0,4)}`)(p.paid_on) : '—'}</td>
                      <td>${p.payment_ref_no || '—'}</td>
                      <td style="font-weight:600;color:#059669">$${parseFloat(p.amount || 0).toFixed(2)}</td>
                      <td>${p.method || '—'}</td>
                      <td>${p.note || '—'}</td>
                    </tr>`).join('');

                  const iframeId = '__etpos_pay_print_iframe';
                  document.getElementById(iframeId)?.remove();
                  const iframe = document.createElement('iframe');
                  iframe.id = iframeId;
                  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
                  document.body.appendChild(iframe);

                  const html = `<!DOCTYPE html><html><head>
                    <meta charset="UTF-8">
                    <title>Payment History - ${sale.invoiceNo || ''}</title>
                    <style>
                      *{margin:0;padding:0;box-sizing:border-box}
                      body{font-family:Arial,sans-serif;font-size:13px;color:#333;padding:24px 28px}
                      h2{font-size:16px;font-weight:700;margin-bottom:4px}
                      .meta{font-size:12px;color:#6b7280;margin-bottom:16px}
                      .info{display:flex;gap:32px;margin-bottom:16px;padding:10px;background:#f8fafc;border-radius:6px}
                      .info-item label{font-size:11px;color:#6b7280;display:block}
                      .info-item span{font-size:13px;font-weight:600;color:#1e293b}
                      table{width:100%;border-collapse:collapse;margin-top:8px}
                      th{background:#10b981;color:#fff;padding:8px 10px;text-align:left;font-size:12px;font-weight:600}
                      td{padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:13px}
                      @media print{body{padding:15px 18px}}
                    </style>
                  </head><body>
                    <h2>Payment History</h2>
                    <div class="meta">Invoice: <strong>${sale.invoiceNo || 'N/A'}</strong></div>
                    <div class="info">
                      <div class="info-item"><label>Customer</label><span>${sale.customer || 'N/A'}</span></div>
                      <div class="info-item"><label>Total Payable</label><span>$${parseFloat(sale.totalPayable || sale.finalTotal || 0).toFixed(2)}</span></div>
                      <div class="info-item"><label>Total Paid</label><span>$${parseFloat(sale.totalPaid || 0).toFixed(2)}</span></div>
                      <div class="info-item"><label>Status</label><span>${sale.paymentStatus || 'Due'}</span></div>
                    </div>
                    <table>
                      <thead><tr><th>#</th><th>Date</th><th>Reference No</th><th>Amount</th><th>Method</th><th>Note</th></tr></thead>
                      <tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#6b7280">No payment records</td></tr>'}</tbody>
                    </table>
                  </body></html>`;

                  const iDoc = iframe.contentWindow.document;
                  iDoc.open();
                  iDoc.write(html);
                  iDoc.close();
                  iframe.onload = () => {
                    setTimeout(() => {
                      iframe.contentWindow.focus();
                      iframe.contentWindow.print();
                      setTimeout(() => iframe.remove(), 1000);
                    }, 300);
                  };
                }}
              >
                🖨️ Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Shipping Modal */}
      {showEditShippingModal && selectedSaleForShipping && (
        <EditShippingModal
          sale={selectedSaleForShipping}
          onClose={() => {
            setShowEditShippingModal(false);
            setSelectedSaleForShipping(null);
          }}
          onUpdate={() => {
            loadSales();
          }}
        />
      )}

      {/* Sell Return Modal */}
      {showSellReturnModal && selectedSaleForReturn && (
        <SellReturnModal
          sale={selectedSaleForReturn}
          locations={businessLocations}
          onClose={() => {
            setShowSellReturnModal(false);
            setSelectedSaleForReturn(null);
          }}
          onSuccess={() => {
            setActiveTab('sales_returns');
            loadSales();
          }}
        />
      )}

      {/* Packing Slip Modal */}
      {showPackingSlipModal && packingSlipData && (
        <div className="modal-overlay packing-slip-overlay" onClick={() => setShowPackingSlipModal(false)}>
          <div className="modal-container packing-slip-modal-container dark-mode-modal" style={{ maxWidth: '700px', width: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Packing Slip</h2>
              <button className="modal-close-btn" onClick={() => setShowPackingSlipModal(false)}>✕</button>
            </div>
            <div className="modal-content packing-slip-modal-content" style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, background: '#fff' }} id="packing-slip-print-area">
              {/* Header: Title + Invoice info on right, logo only if available */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                {/* Left: Business name + logo */}
                <div>
                  {packingSlipData.logoUrl && (
                    <img
                      src={packingSlipData.logoUrl}
                      alt="Business Logo"
                      style={{ maxHeight: '60px', maxWidth: '160px', display: 'block', marginBottom: '6px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{packingSlipData.businessName}</div>
                  <p style={{ fontSize: '12px', color: '#444', whiteSpace: 'pre-line', margin: 0 }}>{packingSlipData.businessAddress}</p>
                </div>
                {/* Right: Packing Slip title + Invoice No + Date */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '20px', fontStyle: 'italic', color: '#555', marginBottom: '4px' }}>Packing Slip</div>
                  <table style={{ marginLeft: 'auto', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ fontWeight: 'bold', paddingRight: '12px', fontSize: '13px' }}>Invoice No.</td>
                        <td style={{ textAlign: 'right', fontSize: '13px' }}>{packingSlipData.invoiceNo}</td>
                      </tr>
                      <tr>
                        <td style={{ fontWeight: 'bold', paddingRight: '12px', fontSize: '13px' }}>Date</td>
                        <td style={{ textAlign: 'right', fontSize: '13px' }}>{packingSlipData.formattedDate}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Customer + Shipping */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', gap: '30px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '3px' }}>Customer</div>
                  <p style={{ fontSize: '12px', margin: '0 0 2px 0' }}>{packingSlipData.customerName}</p>
                  <p style={{ fontSize: '12px', margin: 0 }}><strong>Mobile:</strong> {packingSlipData.customerMobile}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '3px' }}>Shipping Address:</div>
                  <p style={{ fontSize: '12px', margin: 0 }}>{packingSlipData.shippingAddress || '--'}</p>
                </div>
              </div>

              {/* Products Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#10b981', color: 'white', padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: 600, width: '36px' }}>#</th>
                    <th style={{ background: '#10b981', color: 'white', padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: 600 }}>Product</th>
                    <th style={{ background: '#10b981', color: 'white', padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: 600, width: '120px' }}>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {packingSlipData.sellLines.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: '10px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>No products</td></tr>
                  ) : packingSlipData.sellLines.map((item) => (
                    <tr key={item.index}>
                      <td style={{ padding: '7px 10px', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>{item.index}</td>
                      <td style={{ padding: '7px 10px', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>{item.productName}{item.sku ? ' , ' + item.sku : ''}</td>
                      <td style={{ padding: '7px 10px', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>{item.qty} {item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Authorized Signatory */}
              <div style={{ marginTop: '24px' }}>
                <div style={{ width: '180px', borderTop: '1px solid #333', paddingTop: '4px', fontSize: '12px', fontWeight: 'bold' }}>Authorized Signatory</div>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb' }}>
              <button className="btn-secondary" onClick={() => setShowPackingSlipModal(false)}>Close</button>
              <button className="btn-primary" onClick={() => {
                const printContent = document.getElementById('packing-slip-print-area');
                const printWindow = window.open('', '_blank');
                if (printWindow && printContent) {
                  printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="UTF-8">
                        <title>Packing Slip - ${packingSlipData.invoiceNo}</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { font-family: Arial, Helvetica, sans-serif; padding: 30px 40px; background: #fff; color: #333; font-size: 13px; line-height: 1.5; }
                          img { max-height: 80px; max-width: 200px; }
                          table { border-collapse: collapse; }
                          @media print { body { padding: 15px 20px; } }
                        </style>
                      </head>
                      <body>${printContent.innerHTML}</body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.onload = () => { setTimeout(() => printWindow.print(), 300); };
                }
              }}>Print</button>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && selectedSaleForNotification && (
        <SendNotificationModal
          sale={selectedSaleForNotification}
          onClose={() => {
            setShowNotificationModal(false);
            setSelectedSaleForNotification(null);
          }}
          onSuccess={() => {
            console.log('Notification sent successfully');
          }}
        />
      )}

      {/* Filter Modal Popup */}
      {showFilters && (
        <div className="modal-overlay filter-modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal dark-mode-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Business Location:</label>
                  <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
                    <option value="all">All</option>
                    {businessLocations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name} ({location.location_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Customer:</label>
                  <select value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)}>
                    <option value="all">All</option>
                    {/* contacts state from API is more reliable for filtering by ID */}
                    {contacts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.mobile ? `(${c.mobile})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Payment Status:</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="due">Due</option>
                    <option value="partial">Partial</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Filter by date:</label>
                  <DateFilter
                    onDateChange={(range) => setDateRange([range.start, range.end])}
                    initialStartDate={new Date()}
                    initialEndDate={new Date()}
                  />
                </div>
              </div>
              <div className="filter-row">
                <div className="filter-group">
                  <label>User:</label>
                  <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
                    <option value="all">All</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name || ''} {user.last_name || ''} {user.username ? `(${user.username})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Sales Commission Agent:</label>
                  <select value={filterCommissionAgent} onChange={(e) => setFilterCommissionAgent(e.target.value)}>
                    <option value="all">All</option>
                    {uniqueAgents.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Shipping Status:</label>
                  <select value={filterShippingStatus} onChange={(e) => setFilterShippingStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="ordered">Ordered</option>
                    <option value="packed">Packed</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="filter-group" style={{ display: 'flex', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal', height: '100%', marginTop: 'auto', paddingBottom: '10px' }}>
                    <input
                      type="checkbox"
                      checked={filterSubscription}
                      onChange={(e) => setFilterSubscription(e.target.checked)}
                      style={{ marginRight: '8px', width: 'auto' }}
                    />
                    Subscriptions
                  </label>
                </div>
              </div>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Payment Method:</label>
                  <select value={filterPaymentMethod} onChange={(e) => setFilterPaymentMethod(e.target.value)}>
                    <option value="all">All</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Sources:</label>
                  <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pos">POS</option>
                    <option value="woocommerce">Woocommerce</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showLedgerModal && selectedSaleForLedger && (
        <LedgerModal
          sale={selectedSaleForLedger}
          onClose={() => {
            setShowLedgerModal(false);
            setSelectedSaleForLedger(null);
          }}
        />
      )}

      {/* ── Delivery Note Modal ── */}
      {showDeliveryNoteModal && deliveryNoteData && (
        <div className="modal-overlay dn-overlay" onClick={() => setShowDeliveryNoteModal(false)}>
          <div className="dn-modal" onClick={(e) => e.stopPropagation()}>

            {/* Gradient Header */}
            <div className="dn-header">
              <div className="dn-header-left">
                <div className="dn-logo-mark">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2" /><path d="M16 8h4l3 5v3h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
                </div>
                <div>
                  <h2 className="dn-title">DELIVERY NOTE</h2>
                  <p className="dn-subtitle">Transport &amp; Delivery Document</p>
                </div>
              </div>
              <div className="dn-header-right">
                <div className="dn-invoice-badge">
                  <span className="dn-invoice-label">Invoice</span>
                  <span className="dn-invoice-no">#{deliveryNoteData.invoiceNo}</span>
                </div>
                <button className="dn-close-btn" onClick={() => setShowDeliveryNoteModal(false)} title="Close">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="dn-body">

              {/* Date + Status strip */}
              <div className="dn-meta-strip">
                <div className="dn-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  <span>{deliveryNoteData.date}</span>
                </div>
                <div className={`dn-status-pill dn-status-${(deliveryNoteData.shippingStatus || 'pending').toLowerCase()}`}>
                  <span className="dn-status-dot" />
                  {deliveryNoteData.shippingStatus}
                </div>
                <div className="dn-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  <span>Driver: <strong>{deliveryNoteData.deliveryPerson}</strong></span>
                </div>
              </div>

              {/* Info Cards Grid */}
              <div className="dn-info-grid">
                <div className="dn-info-card">
                  <div className="dn-info-card-header">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    Deliver To
                  </div>
                  <div className="dn-info-card-body">
                    <p className="dn-customer-name">{deliveryNoteData.customer}</p>
                    <p className="dn-info-row">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                      {deliveryNoteData.contact}
                    </p>
                    <p className="dn-info-row">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                      {deliveryNoteData.address}
                    </p>
                  </div>
                </div>

                <div className="dn-info-card">
                  <div className="dn-info-card-header">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                    Shipment Details
                  </div>
                  <div className="dn-info-card-body">
                    <div className="dn-detail-row"><span>Invoice No.</span><strong>#{deliveryNoteData.invoiceNo}</strong></div>
                    <div className="dn-detail-row"><span>Delivery Date</span><strong>{new Date().toLocaleDateString()}</strong></div>
                    <div className="dn-detail-row"><span>Items Count</span><strong>{deliveryNoteData.items.length} item{deliveryNoteData.items.length !== 1 ? 's' : ''}</strong></div>
                    <div className="dn-detail-row"><span>Total Qty</span><strong>{deliveryNoteData.items.reduce((a, i) => a + parseFloat(i.qty) || a, 0)}</strong></div>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="dn-section">
                <div className="dn-section-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                  Items to Deliver
                </div>
                <div className="dn-table-wrapper">
                  <table className="dn-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>SKU / Barcode</th>
                        <th>Quantity</th>
                        <th>Condition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryNoteData.items.length > 0 ? deliveryNoteData.items.map((item) => (
                        <tr key={item.index}>
                          <td className="dn-td-center">{item.index}</td>
                          <td><strong>{item.name}</strong></td>
                          <td className="dn-td-sku">{item.sku}</td>
                          <td className="dn-td-center"><span className="dn-qty-badge">{item.qty}</span></td>
                          <td><span className="dn-condition-badge">✓ {item.condition.replace('Good ✓', 'Good')}</span></td>
                        </tr>
                      )) : (
                        <tr><td colSpan="5" className="dn-empty">No items found for this delivery.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Signature Section */}
              <div className="dn-signature-section">
                {['Delivered By', 'Received By'].map((label) => (
                  <div className="dn-signature-box" key={label}>
                    <p className="dn-sig-title">{label}</p>
                    <div className="dn-sig-fields">
                      <div className="dn-sig-row"><span>Name</span><div className="dn-sig-line" /></div>
                      <div className="dn-sig-row"><span>Date</span><div className="dn-sig-line" /></div>
                      <div className="dn-sig-row"><span>Time</span><div className="dn-sig-line" /></div>
                    </div>
                    <div className="dn-sig-block">
                      <p>Signature</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Warning Note */}
              <div className="dn-warning-note">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                <p><strong>Important:</strong> Please inspect all items upon delivery. Report any damages or discrepancies immediately. By signing, you confirm receipt of items in good condition.</p>
              </div>

            </div>

            {/* Footer Actions */}
            <div className="dn-footer">
              <button
                className="dn-btn dn-btn-secondary"
                onClick={() => setShowDeliveryNoteModal(false)}
              >
                Close
              </button>
              <button
                className="dn-btn dn-btn-primary"
                onClick={() => {
                  const d = deliveryNoteData;
                  const rows = d.items.map(item => `
                    <tr>
                      <td style="text-align:center">${item.index}</td>
                      <td><strong>${item.name}</strong></td>
                      <td style="color:#6366f1;font-size:12px">${item.sku}</td>
                      <td style="text-align:center;font-weight:700">${item.qty}</td>
                      <td style="text-align:center;color:#16a34a;font-weight:600">✓ Good</td>
                    </tr>`).join('');
                  const iframeId = '__etpos_dn_iframe';
                  document.getElementById(iframeId)?.remove();
                  const iframe = document.createElement('iframe');
                  iframe.id = iframeId;
                  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
                  document.body.appendChild(iframe);
                  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Delivery Note - ${d.invoiceNo}</title>
                  <style>
                    *{margin:0;padding:0;box-sizing:border-box}
                    body{font-family:'Segoe UI',Arial,sans-serif;color:#1e293b;padding:30px;font-size:13px}
                    .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #8b5cf6;margin-bottom:20px}
                    .app-name{font-size:24px;font-weight:800;color:#8b5cf6}
                    .doc-title{text-align:right}
                    .doc-title h2{font-size:22px;font-weight:800;color:#1e293b;letter-spacing:2px}
                    .doc-title p{color:#64748b;font-size:12px;margin-top:4px}
                    .meta-strip{display:flex;gap:24px;margin-bottom:20px;padding:10px 14px;background:#f8fafc;border-radius:8px;font-size:12px;color:#475569}
                    .status{display:inline-block;padding:3px 10px;border-radius:12px;font-weight:700;font-size:11px;background:#dcfce7;color:#16a34a}
                    .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}
                    .card{border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
                    .card-head{background:#f8fafc;padding:8px 14px;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#64748b;border-bottom:1px solid #e2e8f0}
                    .card-body{padding:12px 14px;font-size:12px;line-height:1.8;color:#334155}
                    .customer-name{font-size:14px;font-weight:700;color:#1e293b;margin-bottom:4px}
                    .detail-row{display:flex;justify-content:space-between;padding:3px 0;font-size:12px}
                    .detail-row span{color:#64748b}
                    .detail-row strong{color:#1e293b}
                    h3{font-size:13px;font-weight:700;color:#1e293b;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #e2e8f0}
                    table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:12px}
                    thead tr{background:linear-gradient(135deg,#8b5cf6,#7c3aed);color:white}
                    th{padding:10px 12px;text-align:left;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
                    td{padding:9px 12px;border-bottom:1px solid #f1f5f9;color:#334155}
                    tbody tr:nth-child(even){background:#f8fafc}
                    .sig-section{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:24px}
                    .sig-box{border:1px solid #e2e8f0;border-radius:8px;padding:16px}
                    .sig-title{font-weight:700;color:#8b5cf6;margin-bottom:12px;font-size:12px;text-transform:uppercase;letter-spacing:.5px}
                    .sig-row{display:flex;gap:8px;align-items:baseline;margin-bottom:8px;font-size:12px;color:#64748b}
                    .sig-line{flex:1;border-bottom:1px solid #94a3b8;min-width:80px}
                    .sig-block{margin-top:20px;padding-top:8px;border-top:2px solid #334155;text-align:center;font-size:11px;color:#64748b;font-style:italic}
                    .warning{margin-top:16px;padding:12px;background:#fef3c7;border-left:4px solid #f59e0b;border-radius:6px;font-size:11px;color:#92400e;line-height:1.6}
                    @media print{body{padding:20px}}
                  </style></head><body>
                  <div class="header">
                    <div><p class="app-name">ET-POS</p><p style="color:#64748b;font-size:11px">Point of Sale System</p></div>
                    <div class="doc-title"><h2>DELIVERY NOTE</h2><p>${d.date}</p></div>
                  </div>
                  <div class="meta-strip">
                    <span>Invoice: <strong>#${d.invoiceNo}</strong></span>
                    <span>Status: <span class="status">${d.shippingStatus}</span></span>
                    <span>Driver: <strong>${d.deliveryPerson}</strong></span>
                  </div>
                  <div class="grid">
                    <div class="card">
                      <div class="card-head">Deliver To</div>
                      <div class="card-body"><p class="customer-name">${d.customer}</p><p>📞 ${d.contact}</p><p>📍 ${d.address}</p></div>
                    </div>
                    <div class="card">
                      <div class="card-head">Shipment Info</div>
                      <div class="card-body">
                        <div class="detail-row"><span>Invoice No.</span><strong>#${d.invoiceNo}</strong></div>
                        <div class="detail-row"><span>Date</span><strong>${new Date().toLocaleDateString()}</strong></div>
                        <div class="detail-row"><span>Items</span><strong>${d.items.length}</strong></div>
                      </div>
                    </div>
                  </div>
                  <h3>Items to Deliver</h3>
                  <table><thead><tr><th>#</th><th>Product Name</th><th>SKU</th><th>Qty</th><th>Condition</th></tr></thead>
                  <tbody>${rows || '<tr><td colspan="5" style="text-align:center;color:#94a3b8">No items</td></tr>'}</tbody></table>
                  <div class="sig-section">
                    <div class="sig-box"><p class="sig-title">Delivered By</p><div class="sig-row"><span>Name</span><div class="sig-line"></div></div><div class="sig-row"><span>Date</span><div class="sig-line"></div></div><div class="sig-block">Signature</div></div>
                    <div class="sig-box"><p class="sig-title">Received By</p><div class="sig-row"><span>Name</span><div class="sig-line"></div></div><div class="sig-row"><span>Date</span><div class="sig-line"></div></div><div class="sig-block">Signature</div></div>
                  </div>
                  <div class="warning"><strong>⚠️ Important:</strong> Please inspect all items upon delivery. Report any damages or discrepancies immediately. By signing, you confirm receipt in good condition.</div>
                  </body></html>`;
                  const iDoc = iframe.contentWindow.document;
                  iDoc.open(); iDoc.write(html); iDoc.close();
                  iframe.onload = () => { setTimeout(() => { iframe.contentWindow.print(); }, 300); };
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                Print Delivery Note
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AllSell;
